import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const STORAGE_KEY = 'chic:card-view';

/**
 * "Ver puesto" control.
 * - Collection mode: switches every product card slideshow to its on-model photo.
 * - Product mode: brings the on-model photo of the product gallery into view.
 * On-model photos are the images whose alt text contains one of the configured keywords.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [productButton] - Shows the default product photos.
 * @property {HTMLButtonElement} [wornButton] - Shows the on-model photos.
 *
 * @extends {Component<Refs>}
 */
class WornViewToggle extends Component {
  /** @type {'product' | 'worn'} */
  #view = 'product';

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.mode !== 'collection') return;

    if (sessionStorage.getItem(STORAGE_KEY) === 'worn') this.setView('worn');

    // Filtering, sorting and infinite scroll replace or append cards: re-apply the view.
    const main = document.getElementById('MainContent') ?? document.body;
    this.#observer = new MutationObserver(() => {
      if (this.#view !== 'worn') return;
      if (this.#frame) cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#applyToCards());
    });
    this.#observer.observe(main, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /**
   * Sets the card view on collection pages.
   * @param {'product' | 'worn'} view - The view to show.
   */
  setView(view) {
    this.#view = view === 'worn' ? 'worn' : 'product';
    sessionStorage.setItem(STORAGE_KEY, this.#view);

    const { productButton, wornButton } = this.refs;
    productButton?.setAttribute('aria-pressed', String(this.#view === 'product'));
    wornButton?.setAttribute('aria-pressed', String(this.#view === 'worn'));

    this.#applyToCards();
  }

  /**
   * Brings the on-model photo of the current product gallery into view.
   */
  showWornMedia() {
    const scope = this.closest('.shopify-section') ?? document;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    // Mobile and carousel galleries: select the slide.
    for (const slideshow of scope.querySelectorAll('media-gallery slideshow-component')) {
      if (!(slideshow instanceof HTMLElement) || !slideshow.checkVisibility()) continue;

      const index = this.#wornIndex(this.#slidesOf(slideshow));
      if (index < 0) continue;

      /** @type {any} */ (slideshow).select?.(index);
      slideshow.scrollIntoView({ behavior, block: 'nearest' });
      return;
    }

    // Desktop grid galleries: scroll to the image.
    for (const item of scope.querySelectorAll('.media-gallery__grid > li')) {
      if (!(item instanceof HTMLElement) || !item.checkVisibility()) continue;
      if (!this.#isWorn(item)) continue;

      item.scrollIntoView({ behavior, block: 'center' });
      return;
    }
  }

  #applyToCards() {
    for (const slideshow of document.querySelectorAll('.card-gallery slideshow-component')) {
      if (!(slideshow instanceof HTMLElement)) continue;

      const target = this.#view === 'worn' ? this.#wornIndex(this.#slidesOf(slideshow)) : 0;
      if (target < 0) continue;

      /** @type {any} */ (slideshow).select?.(target, undefined, { animate: false });
    }
  }

  /**
   * @param {HTMLElement} slideshow
   * @returns {Element[]}
   */
  #slidesOf(slideshow) {
    const slides = /** @type {any} */ (slideshow).slides;
    return Array.isArray(slides) ? slides : [...slideshow.querySelectorAll('slideshow-slide:not([hidden])')];
  }

  /**
   * @param {Element[]} slides
   * @returns {number} The index of the first on-model slide, or -1.
   */
  #wornIndex(slides) {
    return slides.findIndex((slide) => this.#isWorn(slide));
  }

  /**
   * @param {Element} element
   * @returns {boolean} Whether the element contains an image whose alt text matches a keyword.
   */
  #isWorn(element) {
    const keywords = (this.dataset.keywords ?? '').split(',').filter(Boolean);
    const alt = element.querySelector('img')?.getAttribute('alt')?.toLowerCase() ?? '';
    return keywords.some((keyword) => alt.includes(keyword));
  }
}

if (!customElements.get('worn-view-toggle')) {
  customElements.define('worn-view-toggle', WornViewToggle);
}
